/**
 * IdleSessionGate — the 30-minute sign-out, wired into Channels.
 *
 * Warns at 28 minutes, replaces the app at 30, and in one tab only, revokes the
 * session.
 *
 * Two things are specific to Channels.
 *
 * **It does nothing when sign-in is off.** That is the local default (see
 * `src/config.js`), and with no session there is nothing to sign out of. A timer
 * running against a screen that was never signed in would lock a developer out
 * of the machine the screen is being built on.
 *
 * **It carries a context.** The Bindings and Templates panels hold typed
 * message copy and settings that exist nowhere else until Save is pressed. They
 * register a draft saver with the session so that work is written to local
 * storage at the 28-minute warning, not thrown away at the lock.
 */

import React, { useCallback } from 'react';
import { useIdleSession, IdleLockScreen, IdleWarningBanner } from '@cloudsam/ui';
import { useAuth } from '../contexts/AuthContext';
import { IdleContext } from '../contexts/IdleContext';
import { idleClock, IDLE_LOCK_MINUTES } from '../idle-session';
import { REQUIRE_AUTH } from '../config';
import hanko from '../hanko-init';
import SamLogo from './SamLogo';

export default function IdleSessionGate({ children }) {
    const { isAuthenticated } = useAuth();

    const handleLock = useCallback(async ({ owned }) => {
        // Only the tab that claimed the sign-out talks to the server.
        if (!owned) return;
        // Any draft save has already finished — useIdleSession waits for it —
        // because revoking the token mid-save makes that save fail with a 401.
        try {
            await hanko.user.logout();
        } catch (err) {
            // The screen is locked either way, and the server-side idle check
            // is the backstop for a revoke that did not land.
            console.warn('[IdleSessionGate] session revoke failed');
        }
    }, []);

    const idle = useIdleSession({
        clock: idleClock,
        enabled: REQUIRE_AUTH && Boolean(isAuthenticated),
        onLock: handleLock,
    });

    if (idle.isLocked) {
        return (
            <IdleLockScreen
                logo={<SamLogo size={56} showWordmark variant="onWhite" />}
                appName="Channels"
                reason={idle.reason}
                saving={idle.saving}
                saveResults={idle.saveResults}
                contextLabel={idle.contextLabel}
                idleMinutes={IDLE_LOCK_MINUTES}
            />
        );
    }

    return (
        <IdleContext.Provider value={idle}>
            {children}
            {idle.isWarning && (
                <IdleWarningBanner
                    secondsUntilLock={idle.secondsUntilLock}
                    saving={idle.saving}
                    onStaySignedIn={idle.staySignedIn}
                />
            )}
        </IdleContext.Provider>
    );
}
